import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  Chip,
} from '@mui/material';
import { ArrowBack, Email, Refresh } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useApp } from '../../contexts/AppContext';
import { HypeCircleLogo } from '../Branding';

const EmailVerification = ({ email, onVerified, onBack }) => {
  const { addNotification } = useApp();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setErrors({ code: 'Verification code is required' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code.trim(),
      });

      addNotification({
        type: 'success',
        message: 'Email verified successfully! You can now sign in.',
      });
      
      onVerified();
    } catch (error) {
      console.error('Verification error:', error);
      
      let errorMessage = 'Verification failed. Please try again.';
      
      switch (error.name) {
        case 'CodeMismatchException':
          errorMessage = 'Invalid code. Please check and try again.';
          break;
        case 'ExpiredCodeException':
          errorMessage = 'Code expired. Please request a new one.';
          break;
        case 'NotAuthorizedException':
          if (error.message.includes('User cannot be confirmed')) {
            errorMessage = 'Account is already verified. You can sign in now.';
            setTimeout(() => onVerified(), 2000);
            return;
          }
          break;
      }

      setErrors({ code: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    
    try {
      await resendSignUpCode({ username: email });
      addNotification({
        type: 'success',
        message: 'New verification code sent!',
      });
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to resend code. Please try again.',
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Card sx={{ 
      maxWidth: { xs: '100%', sm: 450 }, 
      width: '100%', 
      mx: 'auto', 
      borderRadius: { xs: 0, sm: 3 }, 
      boxShadow: { xs: 0, sm: 3 } 
    }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box textAlign="center" mb={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HypeCircleLogo size="lg" variant="default" />
          </Box>
          <Email sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            We sent a 6-digit code to
          </Typography>
          <Chip label={email} color="primary" variant="outlined" sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Enter the code below to verify your account
          </Typography>
        </Box>

        {errors.code && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.code}
          </Alert>
        )}

        <Box component="form" onSubmit={handleVerify}>
          <TextField
            fullWidth
            label="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            error={!!errors.code}
            disabled={loading}
            autoComplete="one-time-code"
            autoFocus
            sx={{ mb: 3 }}
            placeholder="123456"
            inputProps={{ 
              maxLength: 6,
              style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.5rem' }
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || code.length !== 6}
            sx={{ mb: 2, py: 1.5 }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} color="inherit" />
                Verifying...
              </Box>
            ) : (
              'Verify Email'
            )}
          </Button>

          <Box textAlign="center" mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Didn't receive the code?
            </Typography>
            <Button
              variant="text"
              onClick={handleResend}
              disabled={resendLoading || resendCooldown > 0}
              startIcon={resendLoading ? <CircularProgress size={16} /> : <Refresh />}
              size="small"
            >
              {resendCooldown > 0 
                ? `Resend in ${resendCooldown}s` 
                : resendLoading 
                  ? 'Sending...' 
                  : 'Resend Code'
              }
            </Button>
          </Box>
        </Box>

        <Box textAlign="center">
          <Button
            variant="text"
            onClick={onBack}
            startIcon={<ArrowBack />}
            size="small"
          >
            Back to Sign Up
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmailVerification;